import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import { useTheme } from '@/components/ThemeProvider';
import { useBranding } from '@/App';
import axios from 'axios';
import {
  LayoutDashboard, Users, Activity, Network, FileText,
  LogOut, Shield, AlertCircle, Search, Sun, Moon, Bell,
  DollarSign, ChevronDown, Settings, AlertTriangle, XCircle,
  Info, Clock, Globe, CheckCircle2, Copy, Check, PenLine, CreditCard, Receipt, Mail, MessageSquare, BookOpen, Zap, UserCog, Handshake, ArrowDownToLine, MailOpen
} from 'lucide-react';
import { toast } from 'sonner';

const ALL_MENU_SECTIONS = [
  {
    label: 'OVERVIEW',
    items: [
      { icon: LayoutDashboard, label: 'Dashboard',    path: '/admin',                  supportAllowed: true },
      { icon: Users,           label: 'Merchants',    path: '/admin/merchants',         supportAllowed: true },
      { icon: Handshake,       label: 'Partners',     path: '/admin/partners' },
      { icon: ArrowDownToLine, label: 'Withdrawals',  path: '/admin/partner-withdrawals', badge: 'pendingWithdrawals' },
    ],
  },
  {
    label: 'BILLING',
    items: [
      { icon: Zap, label: 'Plans', path: '/admin/plans' },
      { icon: CreditCard, label: 'Payment Gateway', path: '/admin/payment-gateway' },
      { icon: Receipt, label: 'Payment History', path: '/admin/payment-history' },
      { icon: DollarSign, label: 'TX History', path: '/admin/billing-transactions' },
    ],
  },
  {
    label: 'CONTENT',
    items: [
      { icon: PenLine, label: 'Blogs', path: '/admin/blogs', supportAllowed: true },
    ],
  },
  {
    label: 'MONITORING',
    items: [
      { icon: Activity, label: 'Transactions', path: '/admin/transactions' },
      { icon: Network, label: 'Networks', path: '/admin/networks' },
      { icon: Zap, label: 'Node Health', path: '/admin/node-health', supportAllowed: true },
      { icon: AlertTriangle, label: 'Alerts', path: '/admin/alerts' },
    ],
  },
  {
    label: 'SYSTEM',
    items: [
      { icon: MessageSquare, label: 'Support Tickets', path: '/admin/support', supportAllowed: true },
      { icon: BookOpen, label: 'Knowledge Base', path: '/admin/knowledge-base' },
      { icon: Mail, label: 'Mailer', path: '/admin/mailer' },
      { icon: MailOpen, label: 'Email Logs', path: '/admin/email-logs' },
      { icon: FileText, label: 'Audit Logs', path: '/admin/audit-logs' },
      { icon: AlertCircle, label: 'Error Logs', path: '/admin/error-logs' },
      { icon: UserCog, label: 'Members', path: '/admin/members', adminOnly: true },
    ],
  },
];

const AdminLayout = ({ children, title }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { brand_name, logo_data, logo_dark_data, logo_light_data } = useBranding();
  const activeLogo = (theme === 'dark' ? logo_dark_data : logo_light_data) || logo_data;
  const location = useLocation();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [dollarOpen, setDollarOpen] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [alertSummary, setAlertSummary] = useState({ critical: 0, warning: 0, unread: 0, recent: [] });
  const [paymentNotifs, setPaymentNotifs] = useState([]);
  const [paymentUnread, setPaymentUnread] = useState(0);
  const [pendingWithdrawals, setPendingWithdrawals] = useState(0);

  // Fetch pending partner withdrawals count
  useEffect(() => {
    if (isSupport) return;
    axios.get(`${API}/partner/admin/withdrawals?status=pending`)
      .then(r => setPendingWithdrawals((r.data || []).length))
      .catch(() => {});
  }, [location.pathname]);

  // RBAC: filter menu items based on role
  const isSupport = user?.member_role === 'support';
  const menuSections = ALL_MENU_SECTIONS
    .map(section => ({
      ...section,
      items: section.items.filter(item => {
        if (isSupport) return item.supportAllowed === true;
        if (item.adminOnly) return user?.is_main_admin === true; // Members menu only for main admin
        return true;
      }),
    }))
    .filter(section => section.items.length > 0);

  // Guard: redirect support users away from blocked routes
  // Note: '/admin' is exact-match only to prevent it from matching ALL /admin/* routes
  const SUPPORT_ALLOWED = ['/admin', '/admin/merchants', '/admin/blogs', '/admin/node-health', '/admin/support'];
  React.useEffect(() => {
    if (isSupport) {
      const allowed = SUPPORT_ALLOWED.some(p => {
        if (p === '/admin') return location.pathname === '/admin';
        return location.pathname === p || location.pathname.startsWith(p + '/');
      });
      if (!allowed) navigate('/admin', { replace: true });
    }
  }, [isSupport, location.pathname]);

  const PAYMENT_READ_KEY = 'admin_payment_notif_read_at';

  const fetchPaymentNotifs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/payment-notifications?limit=15`);
      const notifs = data.notifications || [];
      setPaymentNotifs(notifs);
      const lastRead = localStorage.getItem(PAYMENT_READ_KEY);
      const unread = lastRead
        ? notifs.filter(n => (n.paid_at || n.created_at) > lastRead).length
        : notifs.length;
      setPaymentUnread(unread);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchPaymentNotifs();
    const interval = setInterval(fetchPaymentNotifs, 30000);
    return () => clearInterval(interval);
  }, [fetchPaymentNotifs]);

  const copyAlertMessage = (id, message) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(message).catch(() => {
          _fallbackCopy(message);
        });
      } else {
        _fallbackCopy(message);
      }
    } catch {
      _fallbackCopy(message);
    }
    setCopiedId(id);
    toast.success('Message copied to clipboard');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const _fallbackCopy = (text) => {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  };

  const initials = user?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'A';

  const fetchAlertSummary = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/alerts/summary`);
      setAlertSummary(data);
    } catch (err) { /* silent */ }
  }, []);

  useEffect(() => {
    document.title = title ? `${title} | CoinPayUp Admin` : 'CoinPayUp Admin';
  }, [title]);

  useEffect(() => {
    fetchAlertSummary();
    const interval = setInterval(fetchAlertSummary, 30000);
    return () => clearInterval(interval);
  }, [fetchAlertSummary]);

  const handleLogout = () => { logout(); navigate('/'); };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/admin/alerts/read-all`);
      fetchAlertSummary();
    } catch (err) { /* silent */ }
  };

  // Bell color: red if critical, yellow if warning only, green otherwise
  const bellColor = alertSummary.critical > 0
    ? 'text-red-500'
    : alertSummary.warning > 0
      ? 'text-amber-500'
      : 'text-emerald-500';

  const totalActive = alertSummary.critical + alertSummary.warning;

  const formatAlertTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const mins = Math.floor((Date.now() - d) / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const SeverityIcon = ({ severity }) => {
    if (severity === 'critical') return <XCircle className="h-4 w-4 text-red-500 shrink-0" />;
    if (severity === 'warning') return <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />;
    return <Info className="h-4 w-4 text-blue-500 shrink-0" />;
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="w-[240px] bg-[hsl(var(--sidebar-bg))] border-r border-[hsl(var(--sidebar-border))] hidden lg:flex flex-col shrink-0">
        {/* Logo */}
        <div className="h-16 flex items-center px-5 gap-2.5">
          {activeLogo ? (
            <img src={activeLogo} alt={brand_name} className="h-7 max-w-[110px] object-contain" />
          ) : (
            <>
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center shadow-lg shadow-red-500/20">
                <Shield className="h-4 w-4 text-white" />
              </div>
              <div>
                <span className="font-bold text-[15px] text-foreground tracking-tight">{brand_name || 'CoinPayUp'}</span>
                <p className="text-[10px] text-[hsl(var(--sidebar-section))] leading-none mt-0.5">Admin Panel</p>
              </div>
            </>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 pt-4 pb-3 overflow-y-auto">
          {menuSections.map((section) => (
            <div key={section.label} className="mb-5">
              <p className="text-[10px] font-semibold text-[hsl(var(--sidebar-section))] uppercase tracking-[0.12em] px-3 mb-2">
                {section.label}
              </p>
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-150 ${
                        isActive
                          ? 'bg-[hsl(var(--sidebar-active-bg))] text-white shadow-md shadow-indigo-500/20'
                          : 'text-[hsl(var(--sidebar-text))] hover:bg-[hsl(var(--sidebar-hover-bg))] hover:text-foreground'
                      }`}
                      data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <item.icon className="h-[18px] w-[18px]" strokeWidth={isActive ? 2 : 1.5} />
                      <span className="flex-1">{item.label}</span>
                      {item.badge === 'pendingWithdrawals' && pendingWithdrawals > 0 && (
                        <span className="flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none" data-testid="pending-withdrawals-badge">
                          {pendingWithdrawals > 99 ? '99+' : pendingWithdrawals}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col min-h-screen min-w-0">
        {/* Top Bar */}
        <header className="h-14 bg-[hsl(var(--topbar-bg))] border-b border-[hsl(var(--sidebar-border))] px-6 flex items-center justify-between sticky top-0 z-40">
          <div className="flex items-center gap-3 flex-1">
            {/* Search */}
            <div className="relative max-w-md w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search merchants, transactions..."
                className="w-full h-9 pl-9 pr-4 bg-[hsl(var(--search-bg))] border border-[hsl(var(--search-border))] rounded-lg text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 transition-all"
                data-testid="admin-search-input"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Admin badge */}
            <div className="flex items-center gap-1.5 mr-2 px-2.5 py-1 rounded-full bg-red-500/10 border border-red-500/20">
              <Shield className="h-3 w-3 text-red-500" />
              <span className="text-[11px] font-semibold text-red-500">Admin</span>
            </div>

            <div className="h-6 w-px bg-border" />

            {/* Payment Notifications ($ icon) */}
            <div className="relative" data-testid="admin-payment-notifications">
              <button
                onClick={() => {
                  setDollarOpen(!dollarOpen);
                  if (!dollarOpen) {
                    localStorage.setItem(PAYMENT_READ_KEY, new Date().toISOString());
                    setPaymentUnread(0);
                  }
                }}
                className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-[hsl(var(--surface-hover))] transition-colors text-muted-foreground hover:text-foreground relative"
                data-testid="admin-payment-notif-btn"
              >
                <DollarSign className="h-4 w-4" />
                {paymentUnread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white bg-emerald-500 flex items-center justify-center" data-testid="payment-notif-badge">
                    {paymentUnread > 99 ? '99+' : paymentUnread}
                  </span>
                )}
              </button>
              {dollarOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setDollarOpen(false)} />
                  <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-gray-200 dark:border-white/10 rounded-xl shadow-xl z-50 overflow-hidden" data-testid="payment-notifs-dropdown">
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-white/10 flex items-center justify-between">
                      <span className="text-sm font-semibold text-foreground">Payment Received</span>
                      {paymentNotifs.length > 0 && (
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500">
                          {paymentNotifs.length} total
                        </span>
                      )}
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {paymentNotifs.length === 0 ? (
                        <div className="px-4 py-8 text-center">
                          <DollarSign className="h-8 w-8 text-muted-foreground mx-auto mb-2 opacity-40" />
                          <p className="text-sm text-muted-foreground">No payments received yet</p>
                        </div>
                      ) : (
                        paymentNotifs.map((n, i) => (
                          <div key={i} className="px-4 py-3 border-b border-gray-200 dark:border-white/10 last:border-0 hover:bg-muted/50 transition-colors">
                            <div className="flex items-start gap-2.5">
                              <div className="w-7 h-7 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0 mt-0.5">
                                <DollarSign className="h-3.5 w-3.5 text-emerald-500" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-foreground truncate">
                                  {n.received_amount || n.amount} {n.currency}
                                  {n.status === 'overpaid' && <span className="ml-1 text-[10px] bg-amber-500/10 text-amber-500 px-1 rounded">overpaid</span>}
                                </p>
                                <p className="text-xs text-muted-foreground truncate">{n.merchant_name}</p>
                                <p className="text-[11px] text-muted-foreground mt-0.5">
                                  {n.network?.toUpperCase()} &bull; {n.paid_at ? new Date(n.paid_at).toLocaleString() : '—'}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    <div className="px-4 py-2.5 border-t border-gray-200 dark:border-white/10">
                      <button
                        onClick={() => { setDollarOpen(false); navigate('/admin/transactions'); }}
                        className="w-full text-center text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                        data-testid="view-all-payments-btn"
                      >
                        View All Transactions
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Notification bell with dropdown */}
            <div className="relative" data-testid="admin-notifications">
              <button
                onClick={() => { setBellOpen(!bellOpen); if (!bellOpen) markAllRead(); }}
                className={`w-8 h-8 rounded-lg flex items-center justify-center hover:bg-[hsl(var(--surface-hover))] transition-colors relative ${bellColor}`}
                data-testid="admin-notifications-bell"
              >
                <Bell className="h-4 w-4" />
                {totalActive > 0 && (
                  <span className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center ${
                    alertSummary.critical > 0 ? 'bg-red-500' : 'bg-amber-500'
                  }`} data-testid="alert-badge-count">
                    {totalActive > 99 ? '99+' : totalActive}
                  </span>
                )}
              </button>
              {bellOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setBellOpen(false)} />
                  <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-gray-200 dark:border-white/10 rounded-xl shadow-xl z-50 overflow-hidden" data-testid="alerts-dropdown">
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-white/10 flex items-center justify-between">
                      <span className="text-sm font-semibold text-foreground">System Alerts</span>
                      {totalActive > 0 && (
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                          alertSummary.critical > 0 ? 'bg-red-500/10 text-red-500' : 'bg-amber-500/10 text-amber-500'
                        }`}>
                          {totalActive} active
                        </span>
                      )}
                    </div>
                    <div className="max-h-72 overflow-y-auto">
                      {alertSummary.recent.length === 0 ? (
                        <div className="px-4 py-8 text-center">
                          <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
                          <p className="text-sm text-muted-foreground">All systems operational</p>
                        </div>
                      ) : (
                        alertSummary.recent.map((alert) => (
                          <div
                            key={alert.id}
                            className="px-4 py-3 border-b border-gray-200 dark:border-white/10 last:border-0 hover:bg-muted/50 transition-colors"
                            data-testid={`dropdown-alert-${alert.id}`}
                          >
                            <div className="flex items-start gap-3">
                              <SeverityIcon severity={alert.severity} />
                              <div className="flex-1 min-w-0">
                                <button
                                  onClick={(e) => { e.stopPropagation(); copyAlertMessage(alert.id, alert.message); }}
                                  className="group flex items-center gap-1.5 text-left cursor-pointer w-full"
                                  title="Click to copy"
                                  data-testid={`dropdown-copy-${alert.id}`}
                                >
                                  <p className="text-sm text-foreground leading-snug truncate group-hover:text-primary transition-colors">{alert.message}</p>
                                  {copiedId === alert.id
                                    ? <Check className="h-3 w-3 text-emerald-500 shrink-0" />
                                    : <Copy className="h-3 w-3 opacity-0 group-hover:opacity-50 shrink-0 transition-opacity" />}
                                </button>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                                    <Globe className="h-2.5 w-2.5" />{alert.network}
                                  </span>
                                  <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                                    <Clock className="h-2.5 w-2.5" />{formatAlertTime(alert.created_at)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    <div className="px-4 py-2.5 border-t border-gray-200 dark:border-white/10">
                      <button
                        onClick={() => { setBellOpen(false); navigate('/admin/alerts'); }}
                        className="w-full text-center text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                        data-testid="view-all-alerts-btn"
                      >
                        View All Alerts
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-[hsl(var(--surface-hover))] transition-colors text-muted-foreground hover:text-foreground"
              data-testid="admin-theme-toggle"
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>

            {/* User avatar dropdown */}
            <div className="relative ml-1" data-testid="admin-user-menu">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 cursor-pointer"
                data-testid="admin-avatar-btn"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-400 to-rose-500 flex items-center justify-center">
                  <span className="text-white text-[11px] font-bold">{initials}</span>
                </div>
                <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              {userMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-2 w-48 bg-card border border-gray-200 dark:border-white/10 rounded-xl shadow-lg z-50 py-1" data-testid="admin-user-dropdown">
                    <button
                      onClick={() => { setUserMenuOpen(false); navigate('/admin/settings'); }}
                      className="flex items-center gap-2.5 w-full px-4 py-2.5 text-[13px] text-foreground hover:bg-muted transition-colors"
                      data-testid="admin-dropdown-settings"
                    >
                      <Settings className="h-4 w-4 text-muted-foreground" />
                      <span>Settings</span>
                    </button>
                    <button
                      onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                      className="flex items-center gap-2.5 w-full px-4 py-2.5 text-[13px] text-red-500 hover:bg-muted transition-colors"
                      data-testid="admin-dropdown-logout"
                    >
                      <LogOut className="h-4 w-4" />
                      <span>Log out</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page Header */}
        <div className="px-6 pt-6 pb-4">
          <h1 className="text-2xl font-bold text-foreground tracking-tight" data-testid="admin-page-title">{title}</h1>
        </div>

        {/* Page Content */}
        <div className="flex-1 px-6 pb-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
